import React, { useMemo } from 'react';
import cx from 'classnames';

import PlayerHeadshot from 'components/player-headshot';
import Star from 'components/star/star';
import { PlayerSearchSuggestionDto } from 'utils/api/dto';
import { getPlayerDisplayName } from 'utils/helpers/player-profile-utils';
import { FAV_AND_RECENT_TYPE_SCOUT_PLAYER, FAV_AND_RECENT_TYPE_TEAM } from 'widgets/favorites/constant';

import ResultWrapper from '../../search-components/result-wrapper';
import styles from '../../search-components/result-list.module.css';
import { FavoriteToggleHandler, SelectItemHandler } from '../search-dropdown/types';

type RecentPlayerSearchResultProps = {
    processing: boolean;
    players: PlayerSearchSuggestionDto[];
    /** the profiles of the favorite players */
    favorites: { id: number }[];
    isFavorite?: boolean;
    handleAddRecentData: SelectItemHandler;
    handleFavoriteToggle: FavoriteToggleHandler;
    getPhotoUrl?: (player: PlayerSearchSuggestionDto) => string | null;
};

/** The found players of a page search panel */
const RecentPlayerSearchResult = ({
    processing,
    players,
    favorites,
    isFavorite = false,
    handleAddRecentData,
    handleFavoriteToggle,
    getPhotoUrl
}: RecentPlayerSearchResultProps) => {
    const favoriteIds = useMemo(() => new Set(favorites.map(({ id }) => id)), [favorites]);

    return (
        <ResultWrapper processing={processing} isNoData={!players.length}>
            {players.map(player => {
                const { firstNameEn, lastNameEn, lastTeam, playerProfileId, lastTeamId } = player;
                const isFavoritePlayer = isFavorite && favoriteIds.has(playerProfileId);

                return (
                    <div key={playerProfileId} className={styles.row}>
                        <div className={styles.item}>
                            <PlayerHeadshot
                                firstName={firstNameEn}
                                lastName={lastNameEn}
                                name={getPlayerDisplayName(firstNameEn, lastNameEn)}
                                photoUrl={getPhotoUrl ? getPhotoUrl(player) : player.photoHref}
                                size={36}
                            />
                            <div className={styles.names}>
                                <button
                                    type="button"
                                    className={cx(styles.link, styles.linkPrimary)}
                                    onClick={() =>
                                        handleAddRecentData(playerProfileId, FAV_AND_RECENT_TYPE_SCOUT_PLAYER)
                                    }
                                >
                                    {firstNameEn ?? ''}&nbsp;
                                    {lastNameEn ?? ' '}
                                </button>
                                {lastTeamId ? (
                                    <button
                                        type="button"
                                        className={styles.link}
                                        onClick={() => handleAddRecentData(lastTeamId, FAV_AND_RECENT_TYPE_TEAM)}
                                    >
                                        {lastTeam ?? ''}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                        {isFavorite ? (
                            <button
                                type="button"
                                className={styles.favoriteButton}
                                onClick={() =>
                                    handleFavoriteToggle(isFavoritePlayer ? playerProfileId : undefined, {
                                        id: playerProfileId,
                                        type: FAV_AND_RECENT_TYPE_SCOUT_PLAYER
                                    })
                                }
                            >
                                <Star active={isFavoritePlayer} height={20} width={20} />
                            </button>
                        ) : null}
                    </div>
                );
            })}
        </ResultWrapper>
    );
};

export default RecentPlayerSearchResult;
